import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Modal } from '../ui/Modal'

/**
 * Modal component for renaming a conversation
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Function to call when modal should close
 * @param {string} currentTitle - Current conversation title
 * @param {function} onRename - Function to call when rename is confirmed
 * @param {boolean} isRenaming - Whether rename operation is in progress
 */
export const RenameConversationModal = ({
  isOpen,
  onClose,
  currentTitle,
  onRename,
  isRenaming = false,
}) => {
  const [newTitle, setNewTitle] = useState('')
  const [error, setError] = useState('')

  // Reset form when modal opens/closes or title changes
  useEffect(() => {
    if (isOpen) {
      setNewTitle(currentTitle || '')
      setError('')
    }
  }, [isOpen, currentTitle])

  const validateTitle = (title) => {
    const trimmedTitle = title.trim()

    if (!trimmedTitle) {
      return 'Conversation name cannot be empty'
    }

    if (trimmedTitle.length > 100) {
      return 'Conversation name must be 100 characters or less'
    }

    return null
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    const trimmedTitle = newTitle.trim()
    const validationError = validateTitle(trimmedTitle)

    if (validationError) {
      setError(validationError)
      return
    }

    // Don't submit if title hasn't changed
    if (trimmedTitle === currentTitle?.trim()) {
      onClose()
      return
    }

    setError('')
    onRename(trimmedTitle)
  }

  const handleCancel = () => {
    setError('')
    onClose()
  }

  const handleInputChange = (e) => {
    const value = e.target.value
    setNewTitle(value)

    // Clear error when user starts typing
    if (error) {
      setError('')
    }
  }

  const isSubmitDisabled =
    isRenaming || !newTitle.trim() || newTitle.trim() === currentTitle?.trim()

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} closeOnBackdropClick={!isRenaming}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <h2 className="text-lg font-semibold text-white">Rename Conversation</h2>

        <div className="space-y-2">
          <label htmlFor="conversation-name" className="block text-sm font-medium text-gray-300">
            Conversation Name
          </label>
          <input
            id="conversation-name"
            type="text"
            value={newTitle}
            onChange={handleInputChange}
            disabled={isRenaming}
            autoFocus
            className={`w-full px-3 py-2 bg-dark-tertiary border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${
              error ? 'border-red-500' : 'border-gray-600'
            }`}
            placeholder="Enter conversation name..."
            maxLength={100}
          />
          {error && <p className="text-sm text-red-400">{error}</p>}
          <p className="text-xs text-gray-400">{newTitle.length}/100 characters</p>
        </div>

        <div className="flex justify-end space-x-3 pt-2">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isRenaming}
            className="px-4 py-2 text-gray-400 hover:text-white transition-all duration-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitDisabled}
            className="px-4 py-2 bg-dark-secondary border border-dark-border text-white rounded-lg hover:bg-dark-tertiary hover:border-gray-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRenaming ? 'Renaming...' : 'Rename'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

RenameConversationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentTitle: PropTypes.string,
  onRename: PropTypes.func.isRequired,
  isRenaming: PropTypes.bool,
}
